import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiMail, FiTrash2, FiFilter, FiSearch, FiCalendar, 
  FiPhone, FiUser, FiMessageSquare, FiCheck, FiX, FiEye
} from 'react-icons/fi';
import { 
  getContacts, updateContactStatus, deleteContact 
} from '../../utils/adminApi';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [selectedStatus, currentPage]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
        ...(selectedStatus && { status: selectedStatus })
      };
      
      const data = await getContacts(params);
      setContacts(data.contacts);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateContactStatus(id, newStatus);
      fetchContacts();
    } catch (error) {
      console.error('Failed to update contact:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await deleteContact(id);
        fetchContacts();
        setSelectedContact(null);
      } catch (error) {
        console.error('Failed to delete contact:', error);
      }
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-accent-100 text-accent-800';
      case 'read': return 'bg-primary-100 text-primary-800';
      case 'replied': return 'bg-secondary-100 text-secondary-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-heading text-neutral-900 mb-2">
          Contact Management
        </h1>
        <p className="text-neutral-600">
          Manage and respond to customer inquiries and feedback.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="pl-10 input-field"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>

          <div className="flex items-center justify-end">
            <span className="text-sm text-neutral-600">
              Total: {filteredContacts.length} contacts
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contacts List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-primary-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                <p className="mt-2 text-neutral-600">Loading contacts...</p>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="p-8 text-center">
                <FiMail className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">No contacts found</h3>
                <p className="text-neutral-600">
                  {searchTerm || selectedStatus 
                    ? 'Try adjusting your filters' 
                    : 'No contact messages yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {filteredContacts.map((contact) => (
                  <motion.div
                    key={contact.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`p-6 hover:bg-neutral-50 cursor-pointer transition-colors ${
                      selectedContact?.id === contact.id ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                    }`}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <FiUser className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-neutral-900">
                            {contact.name}
                          </h3>
                          <p className="text-xs text-neutral-600">{contact.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}>
                          {contact.status.toUpperCase()}
                        </span>
                        {contact.status === 'new' && (
                          <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <h4 className="text-sm font-medium text-neutral-900 mb-1">
                        {contact.subject}
                      </h4>
                      <p className="text-sm text-neutral-600 line-clamp-2">
                        {contact.message}
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-neutral-500">
                      <div className="flex items-center space-x-2">
                        <FiCalendar className="w-3 h-3" />
                        <span>{new Date(contact.created_at).toLocaleDateString()}</span>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center space-x-1">
                          <FiPhone className="w-3 h-3" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 bg-neutral-50 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-neutral-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Details */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-primary-100 sticky top-6">
            {selectedContact ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-neutral-900">Contact Details</h2>
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-neutral-700">Name</label>
                    <p className="text-neutral-900">{selectedContact.name}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Email</label>
                    <p className="text-neutral-900">{selectedContact.email}</p>
                  </div>

                  {selectedContact.phone && (
                    <div>
                      <label className="text-sm font-medium text-neutral-700">Phone</label>
                      <p className="text-neutral-900">{selectedContact.phone}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Subject</label>
                    <p className="text-neutral-900">{selectedContact.subject}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Message</label>
                    <div className="bg-neutral-50 p-3 rounded-lg mt-1">
                      <p className="text-neutral-700 text-sm whitespace-pre-wrap">
                        {selectedContact.message}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Date</label>
                    <p className="text-neutral-900">
                      {new Date(selectedContact.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700 mb-2 block">Status</label>
                    <select
                      value={selectedContact.status}
                      onChange={(e) => {
                        handleStatusUpdate(selectedContact.id, e.target.value);
                        setSelectedContact({...selectedContact, status: e.target.value});
                      }}
                      className="input-field"
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                    </select>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <a
                      href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                      className="flex-1 btn-primary text-center"
                    >
                      <FiMail className="w-4 h-4 inline mr-1" />
                      Reply
                    </a>
                    <button
                      onClick={() => handleDelete(selectedContact.id)}
                      className="bg-danger-100 text-danger-700 py-2 px-3 rounded-lg hover:bg-danger-200 transition-colors font-medium"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <FiEye className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">Select a Contact</h3>
                <p className="text-neutral-600">
                  Choose a contact from the list to view details and manage the conversation.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;